import { Stack } from "@chakra-ui/react"
import type { GetServerSideProps } from "next"
import type { FC } from "react"
import ErrorBoundary from "@/common/components/ErrorBoundary"
import { ROUTES } from "@/common/constants/routes"
import UpsellLayout from "@/governance/components/stake/upsell/UpsellLayout"
import { fetchGovernanceById } from "@/governance/helpers/fetch"
import {
  fetchOrganizationBySlug,
  fetchTokenById,
} from "@/organization/helpers/fetcher"
import { getOrganizationSocialPreviewImage } from "@/organization/helpers/organization"
import {
  FeatureFlag,
  isFeatureFlagOn,
  OrganizationProvider,
} from "@/organization/providers/OrganizationProvider"
import type {
  FeatureState,
  Maybe,
  Organization,
  OrganizationBySlugQuery,
  TokenQuery,
} from "@/query/graphql"
import { StakeType } from "@/stake/types"
import { spacing } from "@/theme/foundations/spacing"
import { OrganizationMenu } from "@/ui/layouts/main/header/HeaderDAO"
import MainLayout from "@/ui/layouts/main/MainLayout"
import MainLayoutContainer from "@/ui/layouts/main/MainLayoutContainer"
import MainLayoutFooter from "@/ui/layouts/main/MainLayoutFooter"
import MainLayoutHead from "@/ui/layouts/main/MainLayoutHead"
import MainLayoutHeader from "@/ui/layouts/main/MainLayoutHeader"
import { isValidAccountId } from "@/web3/helpers/accountId"
import { isCorrectSlug, isWhitelabel } from "@/whitelabel/hooks/useIsWhiteLabel"
import { getWhiteLabelRoute } from "@/whitelabel/utils/breadcrumb"

type Props = {
  organization: Organization
  isWhiteLabel: boolean
  token: TokenQuery["token"] | null
}

const StakePage: FC<Props> = ({ organization, isWhiteLabel, token }) => {
  const tokenSymbol = token?.symbol ?? "Tokens"

  const title = isWhiteLabel
    ? `${organization.name} | Stake`
    : `Tally | ${organization.name} Stake`

  const preview = {
    title,
    image: getOrganizationSocialPreviewImage(organization),
    description: `Stake your ${tokenSymbol} to earn rewards`,
  }

  return (
    <ErrorBoundary section="Governance > Stake">
      <OrganizationProvider organization={organization}>
        <MainLayout bg="white">
          <MainLayoutHead preview={preview} title={title} />
          <MainLayoutHeader
            activeOrganizationMenu={OrganizationMenu.Stake}
            isWhiteLabel={isWhiteLabel}
            organization={organization}
          />

          <MainLayoutContainer>
            <Stack spacing={spacing.containerGap}>
              <UpsellLayout organizationName={organization.name} />
            </Stack>
          </MainLayoutContainer>
          <MainLayoutFooter isWhiteLabel={isWhiteLabel} />
        </MainLayout>
      </OrganizationProvider>
    </ErrorBoundary>
  )
}

function getStakeTypeFromOrgFeatures(
  features?: Maybe<FeatureState[]>,
): StakeType | null {
  if (isFeatureFlagOn(features, FeatureFlag.ShowLSTStaking)) {
    return StakeType.LIQUID
  }

  if (isFeatureFlagOn(features, FeatureFlag.ShowArbStaking)) {
    return StakeType.NATIVE
  }

  if (isFeatureFlagOn(features, FeatureFlag.ShowUnistaker)) {
    return StakeType.NATIVE
  }

  return null
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query } = context
  const { governanceId: governanceIdParam } = query
  const slug = governanceIdParam as string
  let organization: OrganizationBySlugQuery["organization"] | null

  // Find governance by id and redirect to /gov/:slug
  if (isValidAccountId(slug)) {
    const governance = await fetchGovernanceById(slug)

    if (governance && governance.organization) {
      const route = ROUTES.governance.stake.index(
        governance.organization.slug,
        "",
      )

      return {
        redirect: {
          permanent: false,
          destination: isWhitelabel(context)
            ? getWhiteLabelRoute(route)
            : route,
        },
      }
    }
  }

  if (isWhitelabel(context)) {
    if (!isCorrectSlug(context, slug)) {
      return {
        redirect: {
          permanent: false,
          destination: "/404",
        },
      }
    }
  }

  try {
    organization = await fetchOrganizationBySlug(slug, context)
  } catch (_error) {
    return {
      redirect: {
        permanent: false,
        destination: isWhitelabel(context)
          ? getWhiteLabelRoute(
              ROUTES.governance.stake.index(slug, StakeType.LIQUID),
            )
          : ROUTES.governance.stake.index(slug, StakeType.LIQUID),
      },
    }
  }

  if (!organization) {
    return {
      redirect: {
        permanent: false,
        destination: "/404",
      },
    }
  }
  // Get organization stake type based on org feature-flags
  const activeStakeType = getStakeTypeFromOrgFeatures(organization.features)

  if (activeStakeType) {
    return {
      redirect: {
        permanent: false,
        destination: isWhitelabel(context)
          ? getWhiteLabelRoute(
              ROUTES.governance.stake.index(slug, activeStakeType),
            )
          : ROUTES.governance.stake.index(slug, activeStakeType),
      },
    }
  }

  const token = await fetchTokenById(organization.tokenIds[0])

  // no stake type meaning no ff enabled - return upsell view
  return {
    props: {
      organization,
      isWhiteLabel: isWhitelabel(context),
      token,
    },
  }
}

export default StakePage
